<template>
  <div v-if="companyLoaded" class="d-flex flex-wrap gap-6">
    <v-card v-if="isTrial" class="pa-6 flex-grow-1 flex-center-center flex-column">
      <div><span v-html="$t('PREMIUM.ACCOUNT_IS_TRIAL')"></span> <b class="nobr">{{ $filters.moment(premiumExpireDate, 'll') }}</b></div>
      <v-btn color="accent" rounded size="large" class="mt-4 text-uppercase" @click="openPremium()">
        {{ $t('PREMIUM.SEE_AVAILABLE_OFFERS') }}
      </v-btn>
    </v-card>

    <v-card v-if="canInviteUsers && ! hasOrganization" class="pa-6 text-center">
      <b>{{ $t('MANAGE.CURRENT_SUBSCRIPTIONS') }}</b>
      <div :class="{ 'flex-column': ! (isPremium && ! maxUsersExceeded) }" class="mt-2 d-flex align-center">
        <div>
          <div>{{ $t('MANAGE.SUBSCRIPTIONS') }} {{ $t('ACR.USER') }} : <strong :class="{ 'text-errorred': maxUsersExceeded }">{{ users_count }}/{{ company.maxusers }}</strong></div>
          <div>{{ $t('MANAGE.SUBSCRIPTIONS') }} {{ $t('ACR.CONSULT') }} : <strong :class="{ 'text-errorred': maxConsultExceeded }">{{ consults_count }}/{{ company.maxconsults }}</strong></div>
        </div>
        <v-btn icon v-if="isPremium && ! maxUsersExceeded" :title="$t('PREMIUM.UPDATE_SUBSCRIPTION')" class="ml-4" @click.stop="openPremium()">
          <v-icon color="accent">fas fa-cart-plus</v-icon>
        </v-btn>
        <v-btn v-else color="accent" rounded size="large" class="mt-2" @click.stop="openPremium()">{{ $t('PREMIUM.UPDATE_SUBSCRIPTION') }}</v-btn>
      </div>
    </v-card>

    <!-- Change Offer Team or Business -->
    <v-card v-if="isTrial" class="pa-6 flex-grow-1 flex-center-center flex-column">
      <b>{{ $t('PREMIUM.TRIAL.ACTIVE_OFFER') }}</b>
      <b :class="isBusiness ? 'text-businesscolor' : 'text-teamcolor'" class="ml-1">{{ isBusiness ? 'BUSINESS' : 'TEAM' }}</b>
      <div class="mt-2 text-center">
        <v-btn color="accent" rounded variant="outlined" @click="changeTrialPackage">{{ $t('PREMIUM.TRIAL.CHANGE_YOUR_OFFER') }}</v-btn>
      </div>
    </v-card>

    <div v-if="canInviteUsers" class="w-100">
      <b>{{ $t('MANAGE.INVITE_USER') }}</b>
      <v-card class="pa-6 mt-2">
        <div class="mt-2 d-flex align-center">
          <v-text-field v-model="invitetojoin.email" :label="$t('GLOBAL.EMAIL')" :disabled="maxConsultReached" :messages="invitetojoin.message" :error-messages="invitetojoin.error"
                        type="email" class="mr-4" style="max-width: 250px">
          </v-text-field>
          <btn-toggle-field v-model="invitetojoin.access_right" :disabled="maxConsultReached"
                            :items="[{ value: 'consult', title: $filters.ucfirst($t('ACR.CONSULT')) },
                                     { value: 'user', title: $filters.ucfirst($t('ACR.USER')), disabled: maxUsersReached },
                                     { value: 'company_admin', title: $filters.ucfirst($t('ACR.COMPANY_ADMIN')), disabled: maxUsersReached }]">
          </btn-toggle-field>
          <v-btn :disabled="! invitetojoin.email || invitetojoin.sending" :loading="invitetojoin.sending" color="primary" rounded size="large" class="ml-4"
                 @click="inviteUser()">
            {{ $t('MANAGE.SEND_INVITE') }}
          </v-btn>
        </div>
        <div v-if="pending_invites.length" class="mt-3">
          <b class="text-primary pointer" @click="show_pending_invites = ! show_pending_invites">
            <i :class="show_pending_invites ? 'fa-chevron-down' : 'fa-chevron-right'" class="far" style="min-width: 1em"></i>
            {{ $t('MANAGE.INVITES_PENDING', { count: pending_invites.length }) }}
          </b>
          <v-fade-transition hide-on-leave>
            <table v-if="show_pending_invites">
              <transition-group name="list-fade" tag="tbody">
                <tr v-for="invite in pending_invites" :key="invite.id" class="list-fade-item">
                  <td>{{ invite.email }}</td>
                  <td class="px-6">{{ { consult: $t('ACR.CONSULT'), user: $t('ACR.USER'), company_admin: $t('ACR.COMPANY_ADMIN') }[invite.access_right] }}</td>
                  <td>{{ $filters.moment(invite.date_of_creation, 'calendar') }}</td>
                  <td v-if="userIsAdmin" class="pl-6 pr-2">
                    <v-btn-icon size="small" @click="deletePendingInvite(invite)"><v-icon color="errorred">far fa-trash-alt</v-icon></v-btn-icon>
                  </td>
                  <td v-if="userIsAdmin && invite.date_of_creation < today">
                    <v-btn-icon :disabled="invitetojoin.sending" size="small" @click="resendPendingInvite(invite)"><v-icon color="primary">far fa-paper-plane</v-icon></v-btn-icon>
                  </td>
                </tr>
              </transition-group>
            </table>
          </v-fade-transition>
        </div>

        <premium-section feature="invite-users"></premium-section>
      </v-card>
    </div>
  </div>
</template>

<style>
  .text-teamcolor {
    color: #278bff;
  }
  .text-businesscolor {
    color: #3cd458;
  }
</style>

<script>
  import useUiNavigationStore from '@/stores/ui/navigation';
  import useUiMsgboxStore from '@/stores/ui/msgbox';
  import useLangStore from '@/stores/lang';
  import useUsersStore from '@/stores/users';
  import useUsersAccessRightStore from '@/stores/users/accessRight';

  export default {
    data() {
      return {
        companyLoaded: null,
        company: {},
        company_users: [],
        pending_invites: [],
        invitetojoin: { email: "", access_right: "user", sending: null, message: "", error: "" },
        show_pending_invites: false,
        today: moment().format('YYYY-MM-DD'),
        uiNavigationStore: useUiNavigationStore(),
        uiMsgboxStore: useUiMsgboxStore(),
        langStore: useLangStore(),
        usersStore: useUsersStore(),
        usersAccessRightStore: useUsersAccessRightStore(),
      };
    },
    computed: {
      consults_count() {
        return this.company_users.filter(item => item.access_right == 'consult').length;
      },
      users_count() {
        return this.company_users.length - this.consults_count;
      },
      maxUsersReached() {
        return this.users_count >= this.company.maxusers;
      },
      maxUsersExceeded() {
        return this.users_count > this.company.maxusers;
      },
      maxConsultReached() {
        return (this.consults_count >= this.company.maxconsults) && ((this.users_count + this.consults_count) >= (this.company.maxusers + this.company.maxconsults));
      },
      maxConsultExceeded() {
        return (this.consults_count > this.company.maxconsults) && ((this.users_count + this.consults_count) > (this.company.maxusers + this.company.maxconsults));
      },
      canInviteUsers() {
        return this.userIsAdmin || ! this.user.company;
      },
      premiumExpireDate() {
        return this.user.company.premiumExpireDate;
      },
      user() { return this.usersStore.user; },
      userIsAdmin() { return this.usersAccessRightStore.isAdmin; },
      isPremium() { return this.usersAccessRightStore.isPremium; },
      isBusiness() { return this.usersAccessRightStore.isBusiness; },
      isTrial() { return this.usersAccessRightStore.isTrial; },
      hasOrganization() { return this.usersAccessRightStore.hasOrganization; },
    },
    watch: {
      maxUsersReached(newVal) {
        if (newVal) this.invitetojoin.access_right = "consult";
      },
    },
    created() {
      this.usersStore.userPromise.then(() => {
        if (! this.user.company) {
          this.company = { maxusers: 1, maxconsults: 2 };
        } else {
          this.company = this.user.company;
          if (this.userIsAdmin) this.loadPendingInvites();
        }
        this.company_users = this.usersStore.getCompanyUsers;
        this.companyLoaded = true;
      });
    },
    methods: {
      inviteUser() {
        if (! this.invitetojoin.email) return;
        this.invitetojoin.message = "";
        this.invitetojoin.error = "";
        if (! window.isValidEmailAddress(this.invitetojoin.email)) {
          this.invitetojoin.error = this.$t('MANAGE.INVALID_EMAIL');
          return;
        }
        this.invitetojoin.sending = true;
        window.apiSrv.call('companyinvites/invite', 'store', { email: this.invitetojoin.email, access_right: this.invitetojoin.access_right }).then((response) => {
          this.invitetojoin.message = (response.data && response.data.message) ? response.data.message : this.$t('MANAGE.INVITATION_SENT');
          this.invitetojoin.email = "";
          this.invitetojoin.sending = false;
          this.loadPendingInvites();
        }).catch((message) => {
          this.invitetojoin.error = message || this.$t('MANAGE.INVITATION_NOT_SENT');
          this.invitetojoin.sending = false;
        });
      },
      loadPendingInvites() {
        window.apiSrv.call('companyinvites/pending', 'index').then((response) => {
          this.pending_invites = response && response.data && response.data.invites || [];
          this.pending_invites.sort((a, b) => (a.date_of_creation < b.date_of_creation ? 1 : -1));
        }).catch(() => {});
      },
      deletePendingInvite(invite) {
        window.apiSrv.call('companyinvites', 'destroy', invite.id).then(() => {
          const index = this.pending_invites.indexOf(invite);
          if (index > -1) this.pending_invites.splice(index, 1);
        }).catch((message) => {
          if (message) this.uiMsgboxStore.open({ title: "Error : Invitation was not deleted", body: message || "" });
        });
      },
      resendPendingInvite(invite) {
        this.invitetojoin.email = invite.email;
        this.invitetojoin.access_right = invite.access_right;
        this.inviteUser();
      },
      changeTrialPackage() {
        const packageText = this.isBusiness ? `<b class="text-teamcolor">TEAM</b>` : `<b class="text-businesscolor">BUSINESS</b>`;
        this.uiMsgboxStore.open({
          body: `${this.$t('PREMIUM.TRIAL.CHANGE_OFFER_WARNING_MESSAGE_1')} ${packageText} ${this.$t('PREMIUM.TRIAL.CHANGE_OFFER_WARNING_MESSAGE_2')}`,
          buttons: { ok: 'GLOBAL.OK', cancel: 'GLOBAL.CANCEL' },
        }).then(() => {
          window.apiSrv.call(`companies/${this.user.company_id}`, 'update', { isBusiness: ! this.isBusiness }).then(() => {
            window.location.reload();
          }).catch((message) => {
            if (message) this.uiMsgboxStore.open({ title: "Error : Your offer has not been changed.", body: message });
          });
        }).catch(() => {});
      },
      openPremium() {
        this.uiNavigationStore.setOpenedModal('premium-window');
      },
    },
  };
</script>
